import { useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';
import type { LeaseRepresentation } from '@/common/types/interaction';
import type { MissingFieldsData } from '@/state/navigation';
import type { CoverSheetConfig } from '@/views/Coversheet/configurations/types';
import { missingFieldsByInteractionAtom } from '@/views/Coversheet/state/coverSheetValidation';
import { computeContactMissingFields } from '../computeContactMissingFields';
import { hydrateContacts } from '../shared/hydrate';
import type { ContactsFormData, ContactType } from '../shared/types';

// The dual-lease tenant's-agent card has no `entity`; it renders the shared
// agent as a read-only counterparty-agent summary.
export const DUAL_REP_AGENT_CARD_ID = 'buySideContacts.dualRepBuyersAgent';

const CONTACT_TYPES = Object.keys(
  hydrateContacts([]).contacts,
) as ContactType[];

export function toFormContactType(
  value: string | null | undefined,
): ContactType | undefined {
  return CONTACT_TYPES.find((type) => type === value);
}

/** The contact type a cover-sheet card renders inline, if any. */
export function getCardContactType(card: {
  id: string;
  entity?: string | null;
  entityType?: string | null;
}): ContactType | undefined {
  if (card.id === DUAL_REP_AGENT_CARD_ID) return 'counterpartyAgent';
  return card.entity === 'contacts'
    ? toFormContactType(card.entityType)
    : undefined;
}

export type ContactSectionByType = Partial<Record<ContactType, string>>;

/**
 * Which cover-sheet section (by sidebar label) renders each contact type, from
 * the effective config. `excludeTypes` drops types whose cards the section
 * renderer suppresses (e.g. the buyer's agent and buyer on a client-group
 * offer), so their required fields aren't reported.
 */
export function getContactSectionByType(
  config: CoverSheetConfig,
  { excludeTypes = [] }: { excludeTypes?: ContactType[] } = {},
): ContactSectionByType {
  const excluded = new Set(excludeTypes);
  const byType: ContactSectionByType = {};
  for (const section of config.sections) {
    for (const card of section.cards) {
      const type = getCardContactType(card);
      if (type && !excluded.has(type)) {
        byType[type] = section.sidebarText;
      }
    }
  }
  return byType;
}

/** The config without the contact cards the inline cards replace. */
export function stripContactCards(config: CoverSheetConfig): CoverSheetConfig {
  return {
    ...config,
    sections: config.sections.map((section) => ({
      ...section,
      cards: section.cards.filter((card) => !getCardContactType(card)),
    })),
  };
}

const CONTACT_TYPE_RE = /^contacts\.([a-zA-Z]+)\./;

interface InlineContactMissingFieldsOptions {
  interactionType: string;
  rawState: string | null | undefined;
  representation: LeaseRepresentation | null | undefined;
  sectionByType: ContactSectionByType;
}

/**
 * The contacts' missing required fields, relabelled to the cover-sheet section
 * that renders each contact type so they group under the right sidebar sublink.
 * Fields of a type with no inline card are dropped.
 */
export function computeInlineContactMissingFields(
  formData: ContactsFormData,
  {
    interactionType,
    rawState,
    representation,
    sectionByType,
  }: InlineContactMissingFieldsOptions,
): MissingFieldsData[] {
  return computeContactMissingFields(
    formData,
    interactionType,
    rawState,
    representation,
  ).flatMap((field) => {
    const type = toFormContactType(field.name.match(CONTACT_TYPE_RE)?.[1]);
    const section = type ? sectionByType[type] : undefined;
    return section ? [{ ...field, section }] : [];
  });
}

const isContactFieldName = (name: string) => name.startsWith('contacts.');

/**
 * Replaces the contact entries of a missing-fields record, leaving the
 * property-info entries (owned by the mounted field validators) untouched.
 */
export function mergeContactMissingFields(
  current: Record<string, MissingFieldsData>,
  contactFields: MissingFieldsData[],
): Record<string, MissingFieldsData> {
  const next: Record<string, MissingFieldsData> = {};
  for (const [name, data] of Object.entries(current)) {
    if (!isContactFieldName(name)) next[name] = data;
  }
  for (const field of contactFields) next[field.name] = field;
  return next;
}

export function useMergeContactMissingFields(interactionId: string) {
  return useAtomCallback(
    useCallback(
      (get, set, contactFields: MissingFieldsData[]) => {
        const atom = missingFieldsByInteractionAtom(interactionId);
        set(atom, mergeContactMissingFields(get(atom), contactFields));
      },
      [interactionId],
    ),
  );
}
